import { useState, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ShieldCheck, Check, X, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function SWMValidation({ projectId }) {
  const { isDark } = useTheme();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runValidation = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/validate`);
      setResults(res.data);
      const s = res.data.summary;
      if (s.failed === 0) {
        toast.success(`All ${s.totalRules} validation rules passed!`);
      } else {
        toast.warning(`${s.passed}/${s.totalRules} passed, ${s.totalErrors} total errors`);
      }
    } catch (err) {
      toast.error('Validation failed: ' + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4" data-testid="swm-validation">
      {/* Run Button */}
      <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <button onClick={runValidation} disabled={loading} data-testid="swm-validate-btn"
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg text-white transition-colors"
          style={{ background: loading ? '#94A3B8' : '#F59206' }}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          {loading ? 'Validating...' : 'Run Cross-File Validation'}
        </button>
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          Runs SV-01 through SV-07 validation rules across all imported data
        </span>
      </div>

      {/* Results */}
      {results && (
        <>
          {/* Summary Bar */}
          <div className="flex items-center gap-4 p-3 rounded-lg" data-testid="swm-validation-summary"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Results:</span>
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                style={{ background: '#10B98120', color: '#10B981' }}>
                <Check className="w-3 h-3" /> {results.summary.passed} Passed
              </span>
              {results.summary.failed > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium"
                  style={{ background: '#EF444420', color: '#EF4444' }}>
                  <X className="w-3 h-3" /> {results.summary.failed} Failed
                </span>
              )}
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                ({results.summary.totalErrors} total errors)
              </span>
            </div>
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              Validated: {new Date(results.validatedAt).toLocaleString()}
            </span>
          </div>

          {/* Rule Cards */}
          <div className="space-y-2">
            {Object.entries(results.results).map(([ruleId, rule]) => (
              <div key={ruleId} className="p-3 rounded-lg" data-testid={`swm-rule-${ruleId}`}
                style={{ background: 'var(--card-bg)', border: `1px solid ${rule.status === 'PASS' ? '#10B98140' : '#EF444440'}` }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {rule.status === 'PASS'
                      ? <Check className="w-4 h-4" style={{ color: '#10B981' }} />
                      : <AlertTriangle className="w-4 h-4" style={{ color: '#EF4444' }} />
                    }
                    <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{ruleId}</span>
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{rule.rule}</span>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded font-medium"
                    style={{
                      background: rule.status === 'PASS' ? '#10B98120' : '#EF444420',
                      color: rule.status === 'PASS' ? '#10B981' : '#EF4444',
                    }}>
                    {rule.status} ({rule.errorCount} errors)
                  </span>
                </div>
                {rule.errors.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto rounded p-2 text-[10px] space-y-0.5"
                    style={{ background: 'var(--card-bg-alt)', color: '#EF4444' }}>
                    {rule.errors.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
